import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid2,
    Stack,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonButton from '../../components/atom/CommonButton';
import useNotify from '../../hooks/useNotify';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import { sendTimeslotSeatingLockEmail, useGetTimeslot } from '@openapi/timeslots/timeslots';
import { sendBookingSeatingChangedEmail } from '@openapi/bookings/bookings';
import { useGetShow, useGetArrangementPositions } from '@openapi/shows/shows';
import { applyArrangementOverlay, buildPositionMap } from '../../helpers/arrangementOverlay';
import { useGetBookingItems } from '@openapi/booking-items/booking-items';
import {
    clearTimeslotSeatAssignments,
    useGetFreeTicketSeats,
    useGetTicketSeats,
    getAutoSeating,
} from '@openapi/ticket-seats/ticket-seats';
import { useGetSeatingGroups } from '@openapi/seating-groups/seating-groups';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import SvgViewer from '../../components/svgeditor/SvgViewer';
import mapFromDbToElement from '../seats/helpers/mapFromDbToElement';
import { useSeatDraft } from './useSeatDraft';
import { computeSeatColorMap, normalizeHexColor } from './seatColorMap';
import BookingGroupList from './BookingGroupList';
import SeatingGroupModal from './SeatingGroupModal';
import { UNGROUPED_TABLE_KEY, compareTableEntries } from './seatTables';
import { useCapabilities } from '../../hooks/useCapabilities';

function computeBookingGroups(bookingItems, ticketTypeById) {
    const groups = new Map();
    (bookingItems || []).forEach((item) => {
        if (!groups.has(item.bookingId)) {
            groups.set(item.bookingId, {
                bookingId: item.bookingId,
                booking: item.booking,
                items: [],
            });
        }
        groups.get(item.bookingId).items.push(item);
    });
    return Array.from(groups.values())
        .map((group) => {
            const items = group.items;
            return {
                ...group,
                pax: items.length,
                unassignedCount: items.filter((i) => {
                    if (i.ticketSeatId) return false;
                    const ticketType = ticketTypeById?.[i.ticketTypeId];
                    return Array.isArray(ticketType?.seats) && ticketType.seats.length > 0;
                }).length,
            };
        })
        .filter((group) => group.booking?.status !== 'cancelled')
        .sort((a, b) => {
            if (b.pax !== a.pax) return b.pax - a.pax;
            if (b.unassignedCount !== a.unassignedCount)
                return b.unassignedCount - a.unassignedCount;
            return a.bookingId - b.bookingId;
        });
}

function computeReservedByOtherSeatIds(bookingItems, selectedGroup) {
    const set = new Set();
    (bookingItems || []).forEach((item) => {
        if (!item.ticketSeatId) return;
        if (item.booking?.status === 'cancelled') return;
        if (selectedGroup && item.bookingId === selectedGroup.bookingId) return;
        set.add(item.ticketSeatId);
    });
    return set;
}

const PANEL_SX = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    bgcolor: 'transparent',
};

function SeatAssignmentPage() {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const navigate = useNavigate();
    const { showId, timeslotId } = useParams();
    const numericShowId = Number(showId);
    const numericTimeslotId = Number(timeslotId);

    const { can, CAPABILITIES: CAP } = useCapabilities();
    const canCrossTicketType = can(CAP.SEATING_CROSS_TICKET_TYPE);

    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [isSendingLockEmail, setIsSendingLockEmail] = useState(false);
    const [sendingSeatingChangedBookingIds, setSendingSeatingChangedBookingIds] = useState(
        () => new Set(),
    );
    const [isUnassignDialogOpen, setIsUnassignDialogOpen] = useState(false);
    const [isUnassigning, setIsUnassigning] = useState(false);
    const [seatingGroupModal, setSeatingGroupModal] = useState(null); // { bookingId, seatingGroupId, seatingGroupNote }
    const [seatGroupLoading, setSeatGroupLoading] = useState(null); // groupId being auto-seated

    const {
        data: timeslot,
        isLoading: timeslotLoading,
        isError: timeslotError,
        refetch: refetchTimeslot,
    } = useGetTimeslot(numericTimeslotId, {
        query: {
            enabled: Number.isFinite(numericTimeslotId),
            onError: notifyError,
        },
    });
    const {
        data: show,
        isLoading: showLoading,
        isError: showError,
    } = useGetShow(numericShowId, {
        query: {
            enabled: Number.isFinite(numericShowId),
            onError: notifyError,
        },
    });
    const {
        data: bookingItemsData,
        isLoading: bookingItemsLoading,
        isError: bookingItemsError,
        refetch: refetchBookingItems,
    } = useGetBookingItems(
        { timeslotId: numericTimeslotId, page: 0, pageSize: 10000 },
        {
            query: {
                enabled: Number.isFinite(numericTimeslotId),
                onError: notifyError,
            },
        },
    );
    const {
        data: seatData,
        isLoading: seatLoading,
        isError: seatError,
    } = useGetTicketSeats(numericShowId, { page: 0, pageSize: 10000 });
    const {
        data: freeSeatsData,
        isLoading: freeSeatsLoading,
        isError: freeSeatsError,
        refetch: refetchFreeSeats,
    } = useGetFreeTicketSeats(numericShowId, {
        timeslotId: numericTimeslotId,
        page: 0,
        pageSize: 10000,
    });
    const { data: seatingGroupsData, refetch: refetchSeatingGroups } = useGetSeatingGroups(
        { timeslotId: numericTimeslotId },
        {
            query: {
                enabled: Number.isFinite(numericTimeslotId),
            },
        },
    );

    const {
        data: ticketTypesData,
        isLoading: ticketTypesLoading,
        isError: ticketTypesError,
    } = useGetTicketTypes(
        { optionalId: numericShowId, page: 0, pageSize: 1000, includeSeats: true },
        {
            query: {
                enabled: Number.isFinite(numericShowId),
                onError: notifyError,
            },
        },
    );

    // Per-timeslot override wins, falling back to the show's default arrangement.
    const activeArrangementId = timeslot?.activeArrangementId ?? show?.activeArrangementId ?? null;

    const { data: positionsData } = useGetArrangementPositions(numericShowId, activeArrangementId, {
        query: {
            enabled: Number.isFinite(numericShowId) && !!activeArrangementId,
            onError: notifyError,
        },
    });

    const isLoading =
        timeslotLoading ||
        showLoading ||
        bookingItemsLoading ||
        seatLoading ||
        freeSeatsLoading ||
        ticketTypesLoading ||
        // arrangement is active but its positions have not loaded yet - wait so the
        // map does not briefly render arrangement-excluded seats as available
        (!!activeArrangementId && !positionsData);
    const isError =
        timeslotError ||
        showError ||
        bookingItemsError ||
        seatError ||
        freeSeatsError ||
        ticketTypesError;

    const elements = useMemo(
        () => (seatData?.ticketSeats || []).map(mapFromDbToElement),
        [seatData?.ticketSeats],
    );

    // Active arrangement overlay: display elements + excluded seat ids (Set)
    const { displayElements: viewElements, excludedSeatIds } = useMemo(() => {
        if (!activeArrangementId || !positionsData?.positions) {
            return { displayElements: elements, excludedSeatIds: new Set() };
        }
        const overlay = applyArrangementOverlay(
            elements,
            buildPositionMap(positionsData.positions),
        );
        return {
            displayElements: overlay.displayElements,
            excludedSeatIds: new Set(overlay.excludedSeatIds),
        };
    }, [elements, activeArrangementId, positionsData]);

    const seatNameById = useMemo(() => {
        const map = {};
        elements.forEach((el) => {
            map[el.id] = el.name || `#${el.id}`;
        });
        return map;
    }, [elements]);

    const ticketTypeById = useMemo(() => {
        const map = {};
        (ticketTypesData?.ticketTypes || []).forEach((tt) => {
            map[tt.id] = tt;
        });
        return map;
    }, [ticketTypesData?.ticketTypes]);

    const bookingGroups = useMemo(
        () => computeBookingGroups(bookingItemsData?.bookingItems, ticketTypeById),
        [bookingItemsData?.bookingItems, ticketTypeById],
    );

    const unassignedGroups = useMemo(
        () => bookingGroups.filter((g) => g.unassignedCount > 0),
        [bookingGroups],
    );

    const selectedGroup = useMemo(
        () => bookingGroups.find((g) => g.bookingId === selectedBookingId) || null,
        [bookingGroups, selectedBookingId],
    );

    useEffect(() => {
        if (selectedBookingId && bookingGroups.some((g) => g.bookingId === selectedBookingId))
            return;
        if (unassignedGroups.length) {
            setSelectedBookingId(unassignedGroups[0].bookingId);
        } else if (bookingGroups.length) {
            setSelectedBookingId(bookingGroups[0].bookingId);
        } else {
            setSelectedBookingId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingGroups, unassignedGroups]);

    const freeSeatIds = useMemo(
        () => new Set((freeSeatsData?.ticketSeats || []).map((s) => s.id)),
        [freeSeatsData?.ticketSeats],
    );

    const delayedSeatAssignmentEnabled = !!show?.marketing?.delayedSeatAssignmentEnabled;

    const {
        currentDraftItems,
        suggestedSeatIds,
        selectedDraftSeatIds,
        selectedDraftSeatIdSet,
        otherDraftSeatIdSet,
        otherDraftFreedSeatIdSet,
        currentGroupSavedSeatIdSet,
        getDraftMissingCount,
        hasCrossTypeMismatch,
        totalChangeCount,
        isSuggesting,
        isSaving,
        handleSeatClick,
        handleSuggest,
        handleSave,
        handleClearSeats,
        applyGroupDraftAssignments,
        handleClearGroupSeats,
        draftsByBooking,
    } = useSeatDraft({
        selectedGroup,
        ticketTypeById,
        elements,
        freeSeatIds,
        numericTimeslotId,
        refetchBookingItems,
        refetchFreeSeats,
        bookingItems: bookingItemsData?.bookingItems,
        canCrossTicketType,
    });

    const tableElements = elements.filter((el) => !el.bookable);

    // Precompute seat → nearest table once per render, then both the
    // selected-booking chip list and the timeslot-wide per-booking breakdown
    // reuse it in O(1) instead of scanning tableElements per seat.
    const seatToTable = new Map();
    elements.forEach((seatEl) => {
        if (!seatEl.bookable || tableElements.length === 0) return;
        let bestDist = Infinity;
        let bestId = null;
        let bestName = null;
        tableElements.forEach((tbl) => {
            const dx = seatEl.x - tbl.x;
            const dy = seatEl.y - tbl.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < bestDist) {
                bestDist = d2;
                bestId = tbl.id;
                bestName = tbl.name || `#${tbl.id}`;
            }
        });
        seatToTable.set(seatEl.id, { tableId: bestId, tableName: bestName });
    });
    const nearestTableForSeat = (seatId) =>
        seatToTable.get(seatId) || { tableId: null, tableName: null };

    const seatGroupsByTable = new Map();
    currentDraftItems
        .filter((item) => item.draftTicketSeatId)
        .forEach((item) => {
            const { tableId, tableName } = nearestTableForSeat(item.draftTicketSeatId);
            const key = tableId ?? UNGROUPED_TABLE_KEY;
            if (!seatGroupsByTable.has(key)) {
                seatGroupsByTable.set(key, { tableId: key, tableName, seats: [] });
            }
            seatGroupsByTable.get(key).seats.push({
                id: item.draftTicketSeatId,
                name: seatNameById[item.draftTicketSeatId] || `#${item.draftTicketSeatId}`,
            });
        });
    const assignedSeats = Array.from(seatGroupsByTable.values());

    // Keyed by ticketType so a booking shown in section A is never counted
    // with seats it holds under section B's ticket type.
    // Shape: { [bookingId]: { [ticketTypeId]: [{ tableId, tableName, count }, ...] } }
    const tableAssignmentsByBookingId = {};
    (bookingItemsData?.bookingItems || []).forEach((item) => {
        if (!item.ticketSeatId) return;
        if (item.booking?.status === 'cancelled') return;
        const { tableId, tableName } = nearestTableForSeat(item.ticketSeatId);
        const tKey = tableId ?? UNGROUPED_TABLE_KEY;
        const ttKey = item.ticketTypeId ?? 0;
        if (!tableAssignmentsByBookingId[item.bookingId]) {
            tableAssignmentsByBookingId[item.bookingId] = {};
        }
        const byType = tableAssignmentsByBookingId[item.bookingId];
        if (!byType[ttKey]) byType[ttKey] = new Map();
        if (!byType[ttKey].has(tKey)) {
            byType[ttKey].set(tKey, { tableId: tKey, tableName, count: 0 });
        }
        byType[ttKey].get(tKey).count += 1;
    });
    for (const bookingId of Object.keys(tableAssignmentsByBookingId)) {
        const byType = tableAssignmentsByBookingId[bookingId];
        for (const ttKey of Object.keys(byType)) {
            byType[ttKey] = Array.from(byType[ttKey].values()).sort(compareTableEntries);
        }
    }

    const reservedByOtherSeatIdSet = useMemo(() => {
        const saved = computeReservedByOtherSeatIds(bookingItemsData?.bookingItems, selectedGroup);
        otherDraftFreedSeatIdSet.forEach((id) => saved.delete(id));
        otherDraftSeatIdSet.forEach((id) => saved.add(id));
        return saved;
    }, [
        bookingItemsData?.bookingItems,
        selectedGroup,
        otherDraftSeatIdSet,
        otherDraftFreedSeatIdSet,
    ]);

    const seatIdToBookingId = {};
    (bookingItemsData?.bookingItems || []).forEach((item) => {
        if (item.ticketSeatId && item.booking?.status !== 'cancelled') {
            seatIdToBookingId[item.ticketSeatId] = item.bookingId;
        }
    });

    const handleSeatClickWithReverse = (seatId) => {
        if (!selectedBookingId) {
            const ownerBookingId = seatIdToBookingId[seatId];
            if (ownerBookingId) {
                setSelectedBookingId(ownerBookingId);
                return;
            }
        }
        handleSeatClick(seatId);
    };

    const { colorMap, ticketTypeColorMap } = useMemo(() => {
        const effectiveFreeSeatIds = new Set(freeSeatIds);
        otherDraftFreedSeatIdSet.forEach((id) => effectiveFreeSeatIds.add(id));

        const mismatchSeatIds = new Set();
        const ticketTypes = ticketTypesData?.ticketTypes || [];
        (bookingItemsData?.bookingItems || []).forEach((item) => {
            if (!item.ticketSeatId) return;
            const tt = ticketTypes.find((t) => t.id === item.ticketTypeId);
            if (!tt?.seats?.length) return;
            if (!tt.seats.some((s) => Number(s.id) === Number(item.ticketSeatId))) {
                mismatchSeatIds.add(item.ticketSeatId);
            }
        });
        currentDraftItems.forEach((item) => {
            if (item.crossTypeMismatch && item.draftTicketSeatId) {
                mismatchSeatIds.add(item.draftTicketSeatId);
            }
        });

        return computeSeatColorMap({
            elements,
            ticketTypes,
            selectedDraftSeatIdSet,
            suggestedSeatIds,
            reservedByOtherSeatIdSet,
            freeSeatIds: effectiveFreeSeatIds,
            currentGroupSavedSeatIdSet,
            crossTypeMismatchSeatIds: mismatchSeatIds,
        });
    }, [
        elements,
        ticketTypesData?.ticketTypes,
        selectedDraftSeatIdSet,
        suggestedSeatIds,
        reservedByOtherSeatIdSet,
        freeSeatIds,
        currentGroupSavedSeatIdSet,
        otherDraftFreedSeatIdSet,
        currentDraftItems,
        bookingItemsData?.bookingItems,
    ]);

    const handleOpenSeatingGroupModal = (bookingId, seatingGroupId, seatingGroupNote) => {
        setSeatingGroupModal({ bookingId, seatingGroupId, seatingGroupNote });
    };

    // Map seatingGroupId → { ordinal, name } for display. The ordinal is the
    // group's 1-based position within this timeslot (sorted by id), so labels
    // read "Group 1/2/3" per show instead of the raw global auto-increment id.
    const seatingGroupMeta = {};
    [...(seatingGroupsData?.seatingGroups || [])]
        .sort((a, b) => a.id - b.id)
        .forEach((g, i) => {
            seatingGroupMeta[g.id] = { ordinal: i + 1, name: g.name ?? null };
        });

    const isGroupSeated = (groupId) => {
        const group = (seatingGroupsData?.seatingGroups || []).find((g) => g.id === groupId);
        if (!group) return false;
        return group.bookingIds.every((bId) => {
            const draftMissing = getDraftMissingCount(bId);
            if (draftMissing !== null) return draftMissing === 0;
            const bg = bookingGroups.find((g) => g.bookingId === bId);
            return bg ? bg.unassignedCount === 0 : false;
        });
    };

    const handleSeatGroup = async (groupId) => {
        if (seatGroupLoading) return;
        setSeatGroupLoading(groupId);
        try {
            const group = (seatingGroupsData?.seatingGroups || []).find((g) => g.id === groupId);
            if (!group) return;

            const allItems = (bookingItemsData?.bookingItems || []).filter((item) =>
                group.bookingIds.includes(item.bookingId),
            );

            const requiredMap = new Map();
            const freedSeatIds = [];
            allItems.forEach((item) => {
                if (item.ticketSeatId) freedSeatIds.push(item.ticketSeatId);
                const tt = ticketTypeById[item.ticketTypeId];
                if (!tt?.seats?.length) return;
                requiredMap.set(item.ticketTypeId, (requiredMap.get(item.ticketTypeId) || 0) + 1);
            });

            const requiredSeats = Array.from(requiredMap.entries()).map(
                ([ticketTypeId, quantity]) => ({ ticketTypeId, quantity }),
            );
            if (requiredSeats.length === 0) return;

            // Seats claimed in other bookings' drafts - must be treated as occupied
            const groupBookingIdSet = new Set(group.bookingIds);
            const otherDraftSeats = [];
            Object.entries(draftsByBooking).forEach(([bookingId, items]) => {
                if (groupBookingIdSet.has(Number(bookingId))) return;
                items.forEach((item) => {
                    if (item.draftTicketSeatId) {
                        otherDraftSeats.push({
                            id: Number(item.draftTicketSeatId),
                            ticketTypeId: Number(item.ticketTypeId),
                        });
                    }
                });
            });
            // Also include saved seats from non-draft non-group bookings not yet overridden
            (bookingItemsData?.bookingItems || []).forEach((item) => {
                if (groupBookingIdSet.has(item.bookingId)) return;
                if (!item.ticketSeatId) return;
                const alreadyInDraft = draftsByBooking[item.bookingId];
                if (alreadyInDraft) return; // draft is authoritative for this booking
                otherDraftSeats.push({
                    id: Number(item.ticketSeatId),
                    ticketTypeId: Number(item.ticketTypeId),
                });
            });

            const response = await getAutoSeating(numericTimeslotId, {
                currentSeats: otherDraftSeats,
                requiredSeats,
                freedSeatIds,
            });

            const suggestions = response?.suggestions || [];
            const seatsByType = new Map();
            suggestions.forEach((s) =>
                seatsByType.set(
                    s.ticketTypeId,
                    s.seats.map((seat) => seat.id),
                ),
            );

            const assignments = [];
            const itemsByType = new Map();
            allItems.forEach((item) => {
                const tt = ticketTypeById[item.ticketTypeId];
                if (!tt?.seats?.length) return;
                if (!itemsByType.has(item.ticketTypeId)) itemsByType.set(item.ticketTypeId, []);
                itemsByType.get(item.ticketTypeId).push(item.id);
            });
            itemsByType.forEach((itemIds, ttId) => {
                const seats = seatsByType.get(ttId) || [];
                itemIds.forEach((itemId, i) => {
                    assignments.push({ bookingItemId: itemId, ticketSeatId: seats[i] ?? null });
                });
            });

            applyGroupDraftAssignments(assignments);
        } catch (error) {
            notifyError(
                error?.response?.body?.message || error?.message || t('errors.unknownError'),
            );
        } finally {
            setSeatGroupLoading(null);
        }
    };

    const handleUnseatGroup = (groupId) => {
        const group = (seatingGroupsData?.seatingGroups || []).find((g) => g.id === groupId);
        if (!group) return;
        handleClearGroupSeats(group.bookingIds);
    };

    const handleSendSeatingChangedEmail = async (bookingId) => {
        if (sendingSeatingChangedBookingIds.has(bookingId)) return;
        setSendingSeatingChangedBookingIds((prev) => {
            const next = new Set(prev);
            next.add(bookingId);
            return next;
        });
        try {
            await sendBookingSeatingChangedEmail(bookingId);
            notifySuccess(t('timeslots.assignment.seatingChangedEmailSent'));
            refetchBookingItems();
        } catch (error) {
            notifyError(
                error?.response?.body?.message ||
                    error?.message ||
                    t('errors.failedToSendSeatingChangedEmail'),
            );
        } finally {
            setSendingSeatingChangedBookingIds((prev) => {
                const next = new Set(prev);
                next.delete(bookingId);
                return next;
            });
        }
    };

    const totalAssignedSeatCount = (bookingItemsData?.bookingItems || []).filter(
        (item) =>
            item.ticketSeatId &&
            (item.booking?.status === 'processing' || item.booking?.status === 'finished'),
    ).length;

    const canUnassignAll =
        can(CAP.BOOKINGS_MANAGE) &&
        can(CAP.SEATING_ASSIGNMENTS) &&
        can(CAP.BOOKINGS_SCOPE_ALL) &&
        (delayedSeatAssignmentEnabled || can(CAP.BOOKINGS_OVERBOOK));

    const handleUnassignAll = async () => {
        if (isUnassigning) return;
        setIsUnassigning(true);
        try {
            const result = await clearTimeslotSeatAssignments(numericTimeslotId);
            notifySuccess(
                t('timeslots.assignment.unassignAllSuccess', {
                    count: result?.clearedCount ?? 0,
                }),
            );
            setIsUnassignDialogOpen(false);
            await refetchBookingItems();
            await refetchFreeSeats();
        } catch (error) {
            notifyError(error);
        } finally {
            setIsUnassigning(false);
        }
    };

    const handleSendSeatingLockEmail = async () => {
        setIsSendingLockEmail(true);
        try {
            const result = await sendTimeslotSeatingLockEmail(numericTimeslotId);
            notifySuccess(result?.status || t('timeslots.assignment.seatingLockEmailSent'));
            refetchTimeslot();
        } catch (error) {
            notifyError(error);
        } finally {
            setIsSendingLockEmail(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t('common.loadError')}</Alert>
            </Box>
        );
    }

    const legendEntries = Object.entries(ticketTypeColorMap);
    const saveLabel =
        totalChangeCount > 0 ? `${t('common.save')} (${totalChangeCount})` : t('common.save');

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={{ xs: 1.5, md: 0 }}
                sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'left' }}>
                    {show?.title || ''}{' '}
                    <Typography component="span" variant="h6" color="text.secondary">
                        {timeslot?.startTime ? formatDateTimeInVenueTz(timeslot.startTime) : ''}
                    </Typography>
                </Typography>
                <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
                >
                    {canUnassignAll && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setIsUnassignDialogOpen(true)}
                            disabled={
                                totalAssignedSeatCount === 0 ||
                                isUnassigning ||
                                isSaving ||
                                isSuggesting
                            }
                        >
                            {t('timeslots.assignment.unassignAll')}
                            {totalAssignedSeatCount > 0 ? ` (${totalAssignedSeatCount})` : ''}
                        </Button>
                    )}
                    <CommonButton
                        onClick={handleSave}
                        label={saveLabel}
                        buttonType="primary"
                        disabled={totalChangeCount === 0 || isSaving || isSuggesting}
                    />
                    <CommonButton
                        onClick={() => navigate(`/shows/${showId}/timeslots`)}
                        label={t('common.back')}
                        buttonType="secondary"
                    />
                </Stack>
            </Stack>
            <Dialog
                open={isUnassignDialogOpen}
                onClose={() => !isUnassigning && setIsUnassignDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('timeslots.assignment.unassignAllConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('timeslots.assignment.unassignAllConfirmBody', {
                            count: totalAssignedSeatCount,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsUnassignDialogOpen(false)} disabled={isUnassigning}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleUnassignAll}
                        color="error"
                        variant="contained"
                        disabled={isUnassigning || totalAssignedSeatCount === 0}
                    >
                        {t('timeslots.assignment.unassignAll')}
                    </Button>
                </DialogActions>
            </Dialog>

            {!delayedSeatAssignmentEnabled && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {t('timeslots.assignment.delayedSeatSettingDisabled')}
                </Alert>
            )}

            <Grid2 container spacing={3} sx={{ alignItems: 'stretch' }}>
                <Grid2
                    size={{ xs: 12, md: 8 }}
                    sx={{
                        position: { xs: 'static', md: 'sticky' },
                        top: 80,
                        alignSelf: 'flex-start',
                    }}
                >
                    <Box sx={PANEL_SX}>
                        <SvgViewer
                            elements={viewElements}
                            colorMap={colorMap}
                            onClickElement={handleSeatClickWithReverse}
                            inCartSeatIds={selectedDraftSeatIds}
                            height="calc(100vh - 275px)"
                            mode={activeArrangementId ? 'arrangement' : 'regular'}
                        />
                        {legendEntries.length > 0 && (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    mt: 2,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    bgcolor: 'background.paper',
                                    px: 1.5,
                                    py: 1.5,
                                    borderRadius: 1,
                                }}
                            >
                                {legendEntries.map(([name, color]) => (
                                    <Stack
                                        key={name}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'grey.400',
                                            bgcolor: 'background.paper',
                                            borderRadius: 8,
                                            px: 1.5,
                                            py: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: normalizeHexColor(color),
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: 'text.secondary' }}
                                        >
                                            {name}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            // Desktop bounds the column so the list scrolls internally next to
                            // the sticky map; on mobile the column is unbounded, so let it grow
                            // to its content and scroll with the page instead of clipping.
                            overflow: { xs: 'visible', md: 'hidden' },
                        }}
                    >
                        <BookingGroupList
                            bookingGroups={bookingGroups}
                            unassignedGroups={unassignedGroups}
                            selectedBookingId={selectedBookingId}
                            setSelectedBookingId={setSelectedBookingId}
                            ticketTypeById={ticketTypeById}
                            timeslot={timeslot}
                            onSendSeatingLockEmail={handleSendSeatingLockEmail}
                            isSendingLockEmail={isSendingLockEmail}
                            getDraftMissingCount={getDraftMissingCount}
                            hasCrossTypeMismatch={hasCrossTypeMismatch}
                            onClearSeats={handleClearSeats}
                            delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                            onSendSeatingChangedEmail={handleSendSeatingChangedEmail}
                            sendingSeatingChangedBookingIds={sendingSeatingChangedBookingIds}
                            assignedSeats={assignedSeats}
                            tableAssignmentsByBookingId={tableAssignmentsByBookingId}
                            onSuggest={handleSuggest}
                            onRemoveSeat={handleSeatClick}
                            isSuggesting={isSuggesting}
                            isSaving={isSaving}
                            excludedSeatIds={excludedSeatIds}
                            seatingGroupMeta={seatingGroupMeta}
                            onConnect={handleOpenSeatingGroupModal}
                            onSeatGroup={handleSeatGroup}
                            onUnseatGroup={handleUnseatGroup}
                            isGroupSeated={isGroupSeated}
                            seatGroupLoading={seatGroupLoading}
                        />
                        {seatingGroupModal && (
                            <SeatingGroupModal
                                key={seatingGroupModal.bookingId}
                                open={!!seatingGroupModal}
                                onClose={() => setSeatingGroupModal(null)}
                                bookingId={seatingGroupModal.bookingId}
                                timeslotId={numericTimeslotId}
                                seatingGroupId={seatingGroupModal.seatingGroupId}
                                seatingGroupNote={seatingGroupModal.seatingGroupNote}
                                allGroups={seatingGroupsData?.seatingGroups || []}
                                bookingGroups={bookingGroups}
                                onRefresh={() => {
                                    refetchSeatingGroups();
                                    refetchBookingItems();
                                }}
                            />
                        )}
                    </Box>
                </Grid2>
            </Grid2>
        </Box>
    );
}

export default SeatAssignmentPage;
